import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  useRef,
} from "react";
import { flushSync } from "react-dom";
import Checkbox from "react-custom-checkbox";
import { useTranslation } from "react-i18next";
import { toast } from "../../../../utils/demoToast";
import DishForm from "./DishForm";
import Spinner from "../../../utils/Spinner";
import useApi from "../../../../hooks/useApi";
import {
  creatNewDish,
  updateDish,
  getDish,
  deleteDish,
  getRelatedMenusAndTodaysSpecials,
  switchActivateDish,
} from "../../../../services/dishService";
import Button from "../../../buttons/Button";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import useLangNavigate from "../../../../hooks/useLangNavigate";
import useTour from "../../../../hooks/useTour";
import ProductAppPreview from "../../../app/ProductAppPreview";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/pro-light-svg-icons";
import Modal from "../../../utils/Modal";
import useModal from "../../../../hooks/useModal";
import { usePageTitle } from "../../../../hooks/useMeta";
import NavigationContext from "../../../../contexts/NavigationContext";
import { faCheck } from "@fortawesome/pro-regular-svg-icons";
import { consumptionIds } from "../../../../utils/constants";
import {
  convertDishToFormData as convertDishToForm,
  getDishPreviewImg,
} from "../../../../utils/dishFormUtils";

export const DishView = ({ tKey = "dishView." }) => {
  const { dishId } = useParams();
  usePageTitle(dishId ? "dishView" : "newDish");
  const { t } = useTranslation();
  const navigate = useLangNavigate();
  const { isModalOpen, openModal, closeModal } = useModal();
  const [modalContent, setModalContent] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [initialImgPath, setInitialImgPath] = useState(null);
  const [previewImg, setPreviewImg] = useState(null);
  const [isValid, setIsValid] = useState(false);
  const [copyToCatering, setCopyToCatering] = useState(false);
  const restaurantId = useSelector((state) => state.user.restaurantId);
  const { setHasChanged } = useContext(NavigationContext);
  const { registerSectionGuard } = useTour();
  const dishFormRef = useRef(null);
  const { request: dishCreateRequest, loading: dishCreateLoading } =
    useApi(creatNewDish);
  const { request: dishGetRequest } = useApi(getDish);
  const { request: dishUpdateRequest, loading: dishUpdateLoading } =
    useApi(updateDish);
  const { request: deleteDishRequest } = useApi(deleteDish);
  const { request: switchActivateRequest, loading: switchActivateLoading } =
    useApi(switchActivateDish);
  const { controlledConsumptionModes, controlledRestaurantId } = useSelector(
    (state) => state.admin
  );
  const restaurants = useSelector((state) => state.user.restaurants);

  const showCopyToCatering = () => {
    if (controlledRestaurantId) {
      return controlledConsumptionModes.some((mode) =>
        consumptionIds.CATERING.includes(mode.consumptionModeId)
      );
    } else if (!controlledRestaurantId && restaurantId) {
      const restaurant = restaurants?.find(
        (r) => r.restaurantId === restaurantId
      );
      return restaurant?.consumptionmodes?.some((mode) =>
        consumptionIds.CATERING.includes(mode.consumptionModeId)
      );
    }
    return false;
  };

  const convertDishToFormData = useCallback(
    (dish) => {
      setPreviewImg(getDishPreviewImg(dish, initialImgPath));
      setInitialImgPath(dish.fullImagePath || null);
      setFormData(convertDishToForm(dish, t));
    },
    [initialImgPath, t],
  );

  useEffect(() => {
    setHasChanged(false);
    if (dishId) {
      setIsLoading(true);
      const fetchData = async () => {
        await dishGetRequest(dishId)
          .then((res) => {
            const dish = res.data;
            convertDishToFormData(dish);
            setPreviewImg(dish.fullImagePath || initialImgPath);
          })
          .catch((error) => {
            console.error(error);
            toast.error(error.message);
          })
          .finally(() => {
            setIsLoading(false);
          });
      };
      fetchData();
    }
  }, [
    dishId,
    dishGetRequest,
    t,
    tKey,
    initialImgPath,
    setHasChanged,
    convertDishToFormData,
  ]);

  const attemptCreateDish = useCallback(
    async (e, activate = false) => {
      if (e?.preventDefault) e.preventDefault();
      try {
        await dishCreateRequest({
          ...formData,
          isActive: activate,
          isPublished: true,
          copyToCatering,
          restaurantId,
        });
        toast.success(t(`${tKey}success`));
        navigate("dashboard/products");
        return true;
      } catch (error) {
        toast.error(error.message);
        console.log(error);
        return false;
      }
    },
    [copyToCatering, dishCreateRequest, formData, navigate, restaurantId, t, tKey]
  );

  const attemptUpdateDish = useCallback(
    async (e, activate = false) => {
      if (!!e) e.preventDefault();
      try {
        await dishUpdateRequest({
          ...formData,
          isActive: activate,
          isPublished: true,
          dishId,
          restaurantId,
        });
        toast.success(t(`${tKey}success`));
        navigate("dashboard/products");
        return true;
      } catch (error) {
        toast.error(error.message);
        console.log(error);
        return false;
      }
    },
    [dishId, dishUpdateRequest, formData, navigate, restaurantId, t, tKey]
  );

  const attemptSwitchActiveDish = async () => {
    if (!formData.isActive) await attemptUpdateDish(null, true);
    else
      await switchActivateRequest(dishId)
        .then((res) => {
          convertDishToFormData(res.data);
          toast.success(t(`${tKey}success`));
        })
        .catch((error) => {
          toast.error(error.message);
          console.log(error);
        });
  };

  const attemptDelete = async () => {
    await getRelatedMenusAndTodaysSpecials(dishId).then((res) => {
      const menus = res.data.menus;
      const todaysSpecials = res.data.todaysspecials;
      setModalContent(
        <>
          <h5 className="mt-0">{t(`${tKey}delete.title`)}</h5>
          <p>
            {t(
              `${tKey}delete.${
                menus.length > 0 || todaysSpecials.length > 0
                  ? "textWithRelated"
                  : "text"
              }`,
              { dishName: formData.name }
            )}
          </p>
          {menus.length > 0 && (
            <div>
              {t(`${tKey}delete.menus`, { count: menus.length })}{" "}
              {menus.map((menu, index) => (
                <span key={index}>
                  {menu.name}
                  {index < menus.length - 1 ? ", " : "."}
                </span>
              ))}
            </div>
          )}
          {todaysSpecials.length > 0 && (
            <div>
              {t(`${tKey}delete.todaysSpecials`, {
                count: todaysSpecials.length,
              })}{" "}
              {todaysSpecials.map((todaySpecial, index) => (
                <span key={index}>
                  {todaySpecial.name}
                  {index < todaysSpecials.length - 1 ? ", " : "."}
                </span>
              ))}
            </div>
          )}
        </>
      );
      openModal();
    });
  };

  const deleteCurrentDish = async () => {
    await deleteDishRequest(dishId)
      .then((res) => {
        if (res.status === 200) {
          toast.success(
            t(`${tKey}delete.success`, { dishName: formData.name })
          );
          navigate("dashboard/products");
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error(err.message);
      })
      .finally(async () => {
        setModalContent(null);
        closeModal();
      });
  };

  const handleFormChange = (data) => {
    setHasChanged(true);
    setFormData(data);
    setPreviewImg(
      data.imagePath instanceof File
        ? URL.createObjectURL(data.imagePath)
        : !!data.imagePath
        ? initialImgPath
        : null
    );
  };

  useEffect(() => {
    const unregister = registerSectionGuard("first-dish", async (context) => {
      const isLeavingDishForm =
        context?.reason === "step-transition" &&
        context?.currentRoute === "products/add" &&
        context?.nextRoute === "products";

      if (!isLeavingDishForm) return true;
      if (!isValid) {
        toast.warning(t("dashboard.tour.firstDish.completeFieldsToContinue"));
        flushSync(() => {
          dishFormRef.current?.highlightInvalidFields?.();
        });
        document
          .querySelector(".dish-view-layout .input-error")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
        return false;
      }

      if (dishId) {
        return attemptUpdateDish(null, formData.isActive);
      }
      return attemptCreateDish(null, false);
    });
    return unregister;
  }, [
    attemptCreateDish,
    attemptUpdateDish,
    dishId,
    formData.isActive,
    isValid,
    registerSectionGuard,
    t,
  ]);

  return (
    <div className="dish-view-layout">
      <div className="dashboard-content w-50">
        {isLoading ? (
          <Spinner size="3x" variant="danger" label={t("utils.data.loading")} />
        ) : (
          <>
            <DishForm
              ref={dishFormRef}
              formData={formData}
              dishId={dishId}
              onChange={handleFormChange}
              setIsValid={setIsValid}
              tKey={tKey}
            />
            {!dishId && showCopyToCatering() && (
              <Checkbox
                className="border-dark"
                containerClassName="mt-3 bg-white p-4 rounded"
                icon={
                  <FontAwesomeIcon
                    icon={faCheck}
                    size="sm"
                    className="text-dark"
                  />
                }
                label={t(`${tKey}copyToCatering`)}
                checked={copyToCatering}
                onChange={(value) => {
                  setCopyToCatering(value);
                }}
              />
            )}
          </>
        )}
      </div>
      <div className="dashboard-preview w-50">
        <div className="preview-section">
          <ProductAppPreview
            img={previewImg}
            title={formData?.name !== "" ? formData?.name : null}
            dishCategory={formData?.dishcategory?.value}
          />
        </div>
        <div className="publish-section flex align-center justify-between" data-tour="dish-publish-buttons">
          <div>
            {dishId && (
              <Button variant="white" onClick={attemptDelete}>
                <FontAwesomeIcon size="2x" icon={faTrashAlt} />
              </Button>
            )}
          </div>
          <div className="flex gap-1">
            <Button
              disabled={!isValid}
              onClick={
                dishId
                  ? (e) => attemptUpdateDish(e, formData.isActive)
                  : (e) => attemptCreateDish(e, false)
              }
              loading={
                dishCreateLoading || dishUpdateLoading || switchActivateLoading
              }
            >
              {t(`${tKey}${dishId ? "btnUpdate" : "btnPublish"}`)}
            </Button>
            {dishId ? (
              <Button
                variant={formData?.isActive ? "danger" : "success"}
                disabled={!isValid}
                onClick={attemptSwitchActiveDish}
                loading={
                  dishCreateLoading ||
                  dishUpdateLoading ||
                  switchActivateLoading
                }
              >
                {t(
                  `${tKey}${
                    formData?.isActive ? "btnDeactivate" : "btnActivate"
                  }`
                )}
              </Button>
            ) : (
              <Button
                variant="success"
                disabled={!isValid}
                onClick={
                  dishId
                    ? (e) => attemptUpdateDish(e, formData.isActive)
                    : (e) => attemptCreateDish(e, true)
                }
                loading={
                  dishCreateLoading ||
                  dishUpdateLoading ||
                  switchActivateLoading
                }
              >
                {t(`${tKey}${dishId ? "btnActivate" : "btnPublishActivate"}`)}
              </Button>
            )}
          </div>
        </div>
      </div>
      <Modal isOpen={isModalOpen} onClose={closeModal} onOK={deleteCurrentDish}>
        {modalContent}
      </Modal>
    </div>
  );
};

export default DishView;
